import { withTranslation } from 'react-i18next';
import { compose } from 'recompose';
import React, { Fragment, useRef } from 'react';
import PropTypes from 'prop-types';
import { Map, TileLayer } from 'react-leaflet';
import { MapControls, TemplateStopLegend } from '../../MapComponents';
import * as L from 'leaflet';
import { Checkbox, FormControlLabel, withStyles } from '@material-ui/core';
import styles from './styles';
import { GestureHandling } from 'leaflet-gesture-handling';
import {
  filterTemplatesForDuplicate,
  filterTemplateStopsForCoords,
  filterTemplateStopsForDuplicate,
  filterTemplateStopTemplatesForCoords,
  getElementsWithSamePositionTemplateStop,
  getNumTemplateStopsOnMap,
  getPolyLines,
  getPolyLinesFromTemplateStop,
} from '../../../../services/util/mapUtils';
import useMapControls from '../../../../hooks/useMapControls';
import {
  getColorByType,
  TemplateStopType,
} from 'services/enums/TemplateStopType';
import {
  IconService,
  templateStopConnectionColor,
} from 'services/icon/iconService';
import TemplateStopOverlappingMarkerCluster from 'components/Maps/MapComponents/Marker/TemplateStopOverlappingMarkerCluster';
import TemplateStopTemplateStopMarkerCluster from 'components/Maps/MapComponents/Marker/TemplateStopTemplateStopMarkerCluster';
import TemplateStopTemplateMarkerCluster from 'components/Maps/MapComponents/Marker/TemplateStopTemplateMarkerCluster';
import PolylineCluster from 'components/Maps/MapComponents/Marker/PolylineCluster';

const AddTemplateStopsToTemplateBaseMap = (props) => {
  const {
    classes,
    className,
    templates,
    unassignedTemplateStops,
    displayAllTemplates,
    displayAllTemplatesLabel,
    setDisplayAllTemplates,
    removeAssignedStops,
    setRemoveAssignedStops,
    removeAssignedStopsLabel,
    displayControls,
    addedTemplateStops,
    getPolyLines,
    getUnassignedTemplateStopMarkerAndAddToMcg,
    getTemplateStopMarkerAndAddToMcg,
    getTemplateClusterIcon,
    getToAddTemplateStopMarkerAndAddToMcg,
    getOverLappingMarkerClusterIcon,
    hoveredTemplateStop,
    mapState,
    setMapState,
    t,
  } = props;

  const templateStops = templates.reduce(
    (acc, template) => acc.concat(template.stops),
    []
  );
  const polyLinesPickupDeliveryConnections = hoveredTemplateStop
    ? getPolyLinesFromTemplateStop(
        unassignedTemplateStops
          .concat(addedTemplateStops)
          .concat(templateStops),
        hoveredTemplateStop
      )
    : [];

  const map = useRef(null);

  const handleMapMove = () => {
    const mapElement = map.current.leafletElement;
    const center = mapElement.getCenter();
    const zoom = mapElement.getZoom();
    setMapState({ center: [center.lat, center.lng], zoom });
  };

  const templatesWithStopsWithCoords =
    filterTemplateStopTemplatesForCoords(templates);
  const unassignedTemplateStopsWithCoords = filterTemplateStopsForCoords(
    unassignedTemplateStops
  );
  const addedTemplateStopsWithCoords =
    filterTemplateStopsForCoords(addedTemplateStops);

  const templateStopsOnMap = getNumTemplateStopsOnMap(
    templatesWithStopsWithCoords,
    unassignedTemplateStopsWithCoords,
    addedTemplateStopsWithCoords
  );
  const {
    useCluster,
    setUseCluster,
    useDuplicateSpiderfy,
    setUseDuplicateSpiderfy,
    highLiteDuplicates,
    setHighLiteDuplicates,
  } = useMapControls(templateStopsOnMap);

  const polyLines = getPolyLines(templatesWithStopsWithCoords);

  const overlappingElementGroups = useDuplicateSpiderfy
    ? getElementsWithSamePositionTemplateStop(
        templatesWithStopsWithCoords,
        unassignedTemplateStopsWithCoords,
        addedTemplateStopsWithCoords
      )
    : [];
  const keysOfOverlappingElements = overlappingElementGroups.map(
    (g) => g[0].key
  );

  const filteredTemplates = filterTemplatesForDuplicate(
    templatesWithStopsWithCoords,
    keysOfOverlappingElements
  );
  const filteredUnassignedTemplateStops = filterTemplateStopsForDuplicate(
    unassignedTemplateStopsWithCoords,
    keysOfOverlappingElements
  );
  const filteredAddedTemplateStops = filterTemplateStopsForDuplicate(
    addedTemplateStopsWithCoords,
    keysOfOverlappingElements
  );

  L.Map.addInitHook('addHandler', 'gestureHandling', GestureHandling);

  return (
    <Fragment>
      <Fragment>
        <div className={classes.legendWrapper}>
          <TemplateStopLegend />
        </div>
      </Fragment>
      <Map
        boundsOptions={{ padding: [10, 10] }}
        center={mapState.center}
        className={className}
        gestureHandling
        ref={map}
        zoom={mapState.zoom}
        zoomSnap={0.25}
        onZoomEnd={handleMapMove}
        onMoveEnd={handleMapMove}
      >
        <TileLayer
          attribution='&amp;copy <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <Fragment>
          {filteredTemplates.map((template, index) => (
            <TemplateStopTemplateMarkerCluster
              getClusterIcon={getTemplateClusterIcon}
              getTemplateStopMarkerAndAddToMcg={
                getTemplateStopMarkerAndAddToMcg
              }
              key={index}
              polyline={polyLines[index]}
              templateStops={template.stops}
              template={template}
              useCluster={useCluster}
            />
          ))}
        </Fragment>
        {[TemplateStopType.Pickup, TemplateStopType.Delivery].map(
          (stopType) => (
            <Fragment key={stopType}>
              <Fragment>
                <TemplateStopTemplateStopMarkerCluster
                  clusterIconColor={getColorByType(stopType)}
                  getClusterIcon={(iconColor) =>
                    IconService.getMarkerClusterIcon(iconColor)
                  }
                  getTemplateStopMarkerAndAddToMcg={
                    getUnassignedTemplateStopMarkerAndAddToMcg
                  }
                  templateStops={filteredUnassignedTemplateStops.filter(
                    (templateStop) => templateStop.stopType === stopType
                  )}
                  useCluster={useCluster}
                />
              </Fragment>
              <Fragment>
                <TemplateStopTemplateStopMarkerCluster
                  clusterIconColor={getColorByType(stopType)}
                  getClusterIcon={(iconColor) =>
                    IconService.getMarkerClusterIcon(iconColor)
                  }
                  getTemplateStopMarkerAndAddToMcg={
                    getToAddTemplateStopMarkerAndAddToMcg
                  }
                  templateStops={filteredAddedTemplateStops.filter(
                    (templateStop) => templateStop.stopType === stopType
                  )}
                  useCluster={false}
                />
              </Fragment>
            </Fragment>
          )
        )}
        ,
        {overlappingElementGroups.map((elementGroup, index) => (
          <TemplateStopOverlappingMarkerCluster
            getClusterIcon={getOverLappingMarkerClusterIcon}
            getUnassignedTemplateStopMarkerAndAddToMcg={
              getUnassignedTemplateStopMarkerAndAddToMcg
            }
            getToAddTemplateStopMarkerAndAddToMcg={
              getToAddTemplateStopMarkerAndAddToMcg
            }
            getTemplateStopMarkerAndAddToMcg={getTemplateStopMarkerAndAddToMcg}
            highLiteDuplicates={highLiteDuplicates}
            key={index}
            overlappingElements={elementGroup}
          />
        ))}
        <Fragment>
          <PolylineCluster
            clusterIconColor={templateStopConnectionColor}
            getClusterIcon={(iconColor) =>
              IconService.getMarkerClusterIcon(iconColor)
            }
            useCluster={false}
            polylines={polyLinesPickupDeliveryConnections}
          />
        </Fragment>
      </Map>
      {displayControls && (
        <div>
          <MapControls
            additionalControls={
              <div style={{ marginLeft: '9px' }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={displayAllTemplates}
                      color="secondary"
                      onChange={(event) =>
                        setDisplayAllTemplates(event.target.checked)
                      }
                      value={displayAllTemplates}
                    />
                  }
                  label={
                    displayAllTemplatesLabel
                      ? displayAllTemplatesLabel
                      : t('displayAllTemplates')
                  }
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={removeAssignedStops}
                      color="secondary"
                      onChange={(event) =>
                        setRemoveAssignedStops(event.target.checked)
                      }
                      value={removeAssignedStops}
                      disabled={displayAllTemplates}
                    />
                  }
                  label={
                    removeAssignedStopsLabel
                      ? removeAssignedStopsLabel
                      : t('removeAssignedStops')
                  }
                />
              </div>
            }
            highLiteDuplicates={highLiteDuplicates}
            setHighLiteDuplicates={setHighLiteDuplicates}
            setUseCluster={setUseCluster}
            setUseDuplicateSpiderfy={setUseDuplicateSpiderfy}
            useCluster={useCluster}
            useDuplicateSpiderfy={useDuplicateSpiderfy}
          />
        </div>
      )}
    </Fragment>
  );
};

AddTemplateStopsToTemplateBaseMap.displayName =
  'AddTemplateStopsToTemplateBaseMap';

AddTemplateStopsToTemplateBaseMap.defaultProps = {
  getPolyLines: getPolyLines,
  displayControls: true,
};

AddTemplateStopsToTemplateBaseMap.propTypes = {
  addedTemplateStops: PropTypes.array.isRequired,
  classes: PropTypes.object.isRequired,
  className: PropTypes.string,
  displayControls: PropTypes.bool,
  getOverLappingMarkerClusterIcon: PropTypes.func.isRequired,
  getPolyLines: PropTypes.func,
  getUnassignedTemplateStopMarkerAndAddToMcg: PropTypes.func.isRequired,
  getToAddTemplateStopMarkerAndAddToMcg: PropTypes.func.isRequired,
  getTemplateClusterIcon: PropTypes.func.isRequired,
  getTemplateStopMarkerAndAddToMcg: PropTypes.func.isRequired,
  unassignedTemplateStops: PropTypes.array.isRequired,
  t: PropTypes.func.isRequired,
  templates: PropTypes.array.isRequired,
  hoveredTemplateStop: PropTypes.object,
  displayAllTemplates: PropTypes.bool.isRequired,
  displayAllTemplatesLabel: PropTypes.node,
  setDisplayAllTemplates: PropTypes.func.isRequired,
  removeAssignedStops: PropTypes.bool.isRequired,
  removeAssignedStopsLabel: PropTypes.node,
  setRemoveAssignedStops: PropTypes.func.isRequired,
  mapState: PropTypes.object.isRequired,
  setMapState: PropTypes.func.isRequired,
};

export default compose(
  withStyles(styles),
  withTranslation()
)(AddTemplateStopsToTemplateBaseMap);
